import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  PencilIcon, 
  TrashIcon, 
  LockClosedIcon, 
  LockOpenIcon, 
  KeyIcon,
  EllipsisVerticalIcon 
} from '@heroicons/react/24/outline';
import { Button } from '@/ui/components/atoms/Button';
import { Loading } from '@/ui/components/atoms/Loading';
import { StatusBadge } from '../StatusBadge';
import { RoleBadge } from '../RoleBadge';
import { userManagementAPI } from '../../api/users.api';
import type { User } from '../../types';
import './UserTable.styles.css';

interface UserTableProps {
  users: User[];
  loading: boolean;
  error: Error | null;
  onEdit: (user: User) => void;
  onRefresh: () => void;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  loading,
  error,
  onEdit,
  onRefresh
}) => {
  const { t } = useTranslation();
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const dropdownRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdownId) {
        const dropdownRef = dropdownRefs.current.get(openDropdownId);
        if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
          setOpenDropdownId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  const handleFreeze = async (userId: string) => {
    setActionLoading(`freeze-${userId}`);
    try {
      await userManagementAPI.freezeUser(userId);
      onRefresh();
    } catch (error) {
      console.error('Failed to freeze user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnfreeze = async (userId: string) => {
    setActionLoading(`unfreeze-${userId}`);
    try {
      await userManagementAPI.unfreezeUser(userId);
      onRefresh();
    } catch (error) {
      console.error('Failed to unfreeze user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (userId: string) => {
    const newPassword = prompt(t('admin.userManagement.messages.enterNewPassword', 'Nouveau mot de passe:'));
    if (!newPassword) return;

    setActionLoading(`reset-${userId}`);
    try {
      await userManagementAPI.resetUserPassword(userId, newPassword);
      alert(t('admin.userManagement.messages.passwordReset', 'Mot de passe réinitialisé avec succès'));
    } catch (error) {
      console.error('Failed to reset password:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (userId: string) => {
    const message = t('admin.userManagement.messages.confirmDelete', 
      '⚠️ ATTENTION: Cette action est irréversible!\n\nÊtes-vous sûr de vouloir supprimer définitivement cet utilisateur ?\n\nToutes les données de l\'utilisateur seront perdues.');
    
    if (!confirm(message)) return;

    setActionLoading(`delete-${userId}`);
    try {
      await userManagementAPI.deleteUser(userId);
      onRefresh();
    } catch (error) {
      console.error('Failed to delete user:', error);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <Loading 
        size="large" 
        text={t('admin.userManagement.messages.loadingUsers', 'Chargement des utilisateurs...')} 
      />
    );
  }

  if (error) {
    return (
      <div className="alert alert--error">
        <span>{error.message}</span>
      </div>
    );
  }

  if (!users || users.length === 0) {
    return (
      <div className="empty-state">
        <span>{t('admin.userManagement.messages.noUsers', 'Aucun utilisateur trouvé')}</span>
      </div>
    );
  }

  return (
    <div className="user-table-container">
      <table className="user-table">
        <thead>
          <tr>
            <th>{t('admin.userManagement.table.name', 'Nom')}</th>
            <th>{t('admin.userManagement.table.email', 'Email')}</th>
            <th>{t('admin.userManagement.table.role', 'Rôle')}</th>
            <th>{t('admin.userManagement.table.status', 'Statut')}</th>
            <th>{t('admin.userManagement.table.createdAt', 'Créé le')}</th>
            <th>{t('admin.userManagement.table.actions', 'Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {users.map(user => (
            <tr 
              key={user.id}
              className={
                !user.isActive ? 'user-table--deleted' : 
                user.isFrozen ? 'user-table--frozen' : ''
              }
            >
              <td>
                {user.firstName} {user.lastName}
                {user.role === 'expert' && user.fmName && (
                  <span className="user-fm-name">
                    (FM: {user.fmName})
                  </span>
                )}
              </td>
              <td>{user.email}</td>
              <td>
                <RoleBadge role={user.role} />
              </td>
              <td>
                <StatusBadge user={user} />
              </td>
              <td>
                {user.createdAt ?
                  new Date(user.createdAt).toLocaleDateString('fr-BE') :
                  '-'
                }
              </td>
              <td>
                {user.isActive ? (
                  <div className="user-actions">
                    <div 
                      className="user-actions__dropdown"
                      ref={(el) => {
                        if (el) dropdownRefs.current.set(user.id, el);
                        else dropdownRefs.current.delete(user.id);
                      }}
                    >
                      <Button
                        variant="secondary"
                        size="small"
                        className="user-actions__dropdown-trigger"
                        onClick={() => onEdit(user)}
                        disabled={actionLoading !== null}
                      >
                        <PencilIcon className="icon-small" />
                        <span className="btn-text">
                          {t('admin.userManagement.actions.edit', 'Modifier')}
                        </span>
                      </Button>
                      
                      <button
                        className="user-actions__more-btn"
                        onClick={() => setOpenDropdownId(openDropdownId === user.id ? null : user.id)}
                        disabled={actionLoading !== null}
                        aria-label="More actions"
                      >
                        <EllipsisVerticalIcon className="icon-small" />
                      </button>
                      
                      {openDropdownId === user.id && (
                        <div className="user-actions__dropdown-menu">
                          {!user.isFrozen ? (
                            <button
                              className="dropdown-item"
                              onClick={() => {
                                handleFreeze(user.id);
                                setOpenDropdownId(null);
                              }}
                              disabled={actionLoading !== null}
                            >
                              <LockClosedIcon className="icon-small" />
                              <span>{t('admin.userManagement.actions.freeze', 'Geler')}</span>
                            </button>
                          ) : (
                            <button
                              className="dropdown-item"
                              onClick={() => {
                                handleUnfreeze(user.id);
                                setOpenDropdownId(null);
                              }}
                              disabled={actionLoading !== null}
                            >
                              <LockOpenIcon className="icon-small" />
                              <span>{t('admin.userManagement.actions.unfreeze', 'Dégeler')}</span>
                            </button>
                          )}
                          
                          <button
                            className="dropdown-item"
                            onClick={() => {
                              handleResetPassword(user.id);
                              setOpenDropdownId(null);
                            }}
                            disabled={actionLoading !== null}
                          >
                            <KeyIcon className="icon-small" />
                            <span>{t('admin.userManagement.actions.resetPassword', 'Réinitialiser MDP')}</span>
                          </button>
                          
                          <div className="dropdown-divider" />
                          
                          <button
                            className="dropdown-item dropdown-item--danger"
                            onClick={() => {
                              handleDelete(user.id);
                              setOpenDropdownId(null);
                            }}
                            disabled={actionLoading !== null}
                          >
                            <TrashIcon className="icon-small" />
                            <span>{t('admin.userManagement.actions.delete', 'Supprimer')}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <span className="user-deleted-label">
                    {t('admin.userManagement.messages.userDeleted', 'Utilisateur supprimé')}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};